
import { useState } from 'react';
import { motion } from 'framer-motion';
import { ChartContainer } from "@/components/ui/chart";
import { Bar, BarChart, CartesianGrid, Cell, Legend, Rectangle, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

// Mock data - In a real implementation, this would come from an API
const mockTeamData = {
  engineering: {
    O: 3.8, C: 3.9, E: 2.9, A: 3.5, N: 2.7,
    participants: 15
  },
  marketing: {
    O: 4.1, C: 3.4, E: 4.2, A: 3.8, N: 2.9,
    participants: 8
  },
  design: {
    O: 4.3, C: 3.7, E: 3.5, A: 3.9, N: 3.0,
    participants: 7
  },
  operations: {
    O: 3.4, C: 4.2, E: 3.1, A: 3.6, N: 2.5,
    participants: 12
  },
  sales: {
    O: 3.6, C: 3.5, E: 4.3, A: 3.7, N: 3.1,
    participants: 10
  }
};

interface PersonalityAggregateChartProps {
  companyId?: string;
}

export default function PersonalityAggregateChart({ companyId }: PersonalityAggregateChartProps) {
  const [selectedTeam, setSelectedTeam] = useState<string>('all');
  
  // Chart configuration for colors
  const chartConfig = {
    O: { color: '#9b87f5' }, // Purple
    C: { color: '#60a5fa' }, // Blue
    E: { color: '#f97316' }, // Orange
    A: { color: '#34d399' }, // Green
    N: { color: '#f87171' }, // Red
  };
  
  // Prepare data for visualization
  const prepareData = () => {
    if (selectedTeam === 'all') {
      // Aggregate all teams
      const teams = Object.keys(mockTeamData);
      return [
        {
          name: 'Abertura (O)',
          value: teams.reduce((sum, team) => sum + mockTeamData[team as keyof typeof mockTeamData].O, 0) / teams.length,
          color: '#9b87f5', // Purple
        },
        {
          name: 'Conscienciosidade (C)',
          value: teams.reduce((sum, team) => sum + mockTeamData[team as keyof typeof mockTeamData].C, 0) / teams.length,
          color: '#60a5fa', // Blue
        },
        {
          name: 'Extroversão (E)',
          value: teams.reduce((sum, team) => sum + mockTeamData[team as keyof typeof mockTeamData].E, 0) / teams.length,
          color: '#f97316', // Orange
        },
        {
          name: 'Amabilidade (A)',
          value: teams.reduce((sum, team) => sum + mockTeamData[team as keyof typeof mockTeamData].A, 0) / teams.length,
          color: '#34d399', // Green
        },
        {
          name: 'Neuroticismo (N)',
          value: teams.reduce((sum, team) => sum + mockTeamData[team as keyof typeof mockTeamData].N, 0) / teams.length,
          color: '#f87171', // Red
        },
      ];
    } else {
      // Selected team data
      const teamData = mockTeamData[selectedTeam as keyof typeof mockTeamData];
      if (!teamData) return [];
      
      return [
        { name: 'Abertura (O)', value: teamData.O, color: '#9b87f5' },
        { name: 'Conscienciosidade (C)', value: teamData.C, color: '#60a5fa' },
        { name: 'Extroversão (E)', value: teamData.E, color: '#f97316' },
        { name: 'Amabilidade (A)', value: teamData.A, color: '#34d399' },
        { name: 'Neuroticismo (N)', value: teamData.N, color: '#f87171' },
      ];
    }
  };

  const data = prepareData();
  
  // Get participant count
  const getParticipantCount = () => {
    if (selectedTeam === 'all') {
      return Object.values(mockTeamData).reduce((sum, team) => sum + team.participants, 0);
    }
    return mockTeamData[selectedTeam as keyof typeof mockTeamData]?.participants || 0;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>Perfil de Personalidade da Equipe</CardTitle>
              <CardDescription>
                Média dos traços de personalidade Big Five
                {selectedTeam !== 'all' && ` - Equipe ${selectedTeam}`}
              </CardDescription>
            </div>
            
            <Select
              value={selectedTeam}
              onValueChange={setSelectedTeam}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Selecionar equipe" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as Equipes</SelectItem>
                <SelectItem value="engineering">Engenharia</SelectItem>
                <SelectItem value="marketing">Marketing</SelectItem>
                <SelectItem value="design">Design</SelectItem>
                <SelectItem value="operations">Operações</SelectItem>
                <SelectItem value="sales">Vendas</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <div className="h-80 w-full">
            <ChartContainer config={chartConfig} className="h-full">
              <BarChart data={data}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" />
                <YAxis domain={[0, 5]} tickCount={6} />
                <Tooltip 
                  formatter={(value: number) => [`${value.toFixed(1)} / 5`, 'Pontuação']}
                  labelStyle={{ fontWeight: 'bold' }}
                />
                <Bar 
                  dataKey="value" 
                  activeBar={<Rectangle fill="#13265a" />}
                >
                  {data.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ChartContainer>
          </div>
          
          <div className="mt-4 text-sm text-gray-500 text-center">
            Dados baseados em {getParticipantCount()} participantes
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}
